import { ComputedTab, NavigationTabsOption } from '@common/types/link';
import { isUrlActive } from '@common/utils/urlMatcher';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const useTabUrl = <T extends string>(
    options: NavigationTabsOption<T>[],
    defaultValue?: T,
    onTabChange?: (newTab: T) => void,
    preserveKeys: string[] = ['search'],
) => {
    const { pathname, search } = useLocation();
    const searchParams = new URLSearchParams(search);

    const fallbackValue = defaultValue ?? options[0]?.value;
    const activeQueryKey = options.find((opt) => opt.queryKey)?.queryKey;

    const getCurrentTab = (key?: string): T => {
        const k = key ?? activeQueryKey;
        if (!k) return fallbackValue;
        return (searchParams.get(k) as T) || fallbackValue;
    };

    const currentTab = getCurrentTab();
    const previousTabRef = useRef<T>(currentTab);

    useEffect(() => {
        if (onTabChange && previousTabRef.current !== currentTab) {
            onTabChange(currentTab);
            previousTabRef.current = currentTab;
        }
    }, [currentTab, onTabChange]);

    const getTabState = (option: NavigationTabsOption<T>): ComputedTab<T> => {
        const { queryKey, value, href, pattern, end } = option;

        const to = queryKey
            ? (() => {
                  const params = new URLSearchParams();
                  preserveKeys.forEach((key) => {
                      const val = searchParams.get(key);
                      if (val) params.set(key, val);
                  });

                  params.set(queryKey, value);
                  return `${pathname}?${params.toString()}`;
              })()
            : (href ?? pathname);

        const isDefaultState =
            queryKey && !searchParams.has(queryKey) && value === fallbackValue;

        const isActive =
            isDefaultState ||
            isUrlActive({
                pathname,
                search,
                targetTo: to,
                pattern,
                end,
            });

        return { option, to, isActive };
    };

    return { getTabState, getCurrentTab, currentTab };
};
